import { Recipe } from '../types/recipe';

// Liste manuelle des IDs de recettes diététiques
// Ajoutez ou supprimez les IDs des recettes que vous voulez dans cette section
const DIET_RECIPE_IDS = [
  // Ajoutez ici les IDs des recettes diététiques
  // Exemple: 'recipe-1', 'recipe-2', etc.
];

// Fonction utilitaire pour obtenir tous les IDs de recettes disponibles
// Utilisez cette fonction en console pour voir les IDs disponibles
export const getAllRecipeIds = (recipes: Recipe[]): string[] => {
  return recipes.map(recipe => recipe.id);
};

// Fonction utilitaire pour ajouter une recette diététique
export const addDietRecipe = (recipeId: string): void => {
  if (!DIET_RECIPE_IDS.includes(recipeId)) {
    DIET_RECIPE_IDS.push(recipeId);
    console.log(`Recette ${recipeId} ajoutée aux recettes diététiques`);
  }
};

// Fonction utilitaire pour supprimer une recette diététique
export const removeDietRecipe = (recipeId: string): void => {
  const index = DIET_RECIPE_IDS.indexOf(recipeId);
  if (index > -1) {
    DIET_RECIPE_IDS.splice(index, 1);
    console.log(`Recette ${recipeId} supprimée des recettes diététiques`);
  }
};

// Fonction pour obtenir la liste actuelle des recettes diététiques
export const getDietRecipeIds = (): string[] => {
  return [...DIET_RECIPE_IDS];
};

export type ThemeCategory = 
  | 'express' 
  | 'enfants' 
  | 'sucrees' 
  | 'economiques' 
  | 'vegetariennes' 
  | 'traditionnelles' 
  | 'emporter'
  | 'dietetiques';

export const filterRecipesByTheme = (recipes: Recipe[], theme: ThemeCategory): Recipe[] => {
  switch (theme) {
    case 'express':
      // Recettes avec temps total <= 15 minutes
      return recipes.filter(recipe => {
        const prepTime = parseInt(recipe.prepTime.replace(/\D/g, '')) || 0;
        const cookTime = parseInt(recipe.cookTime.replace(/\D/g, '')) || 0;
        return (prepTime + cookTime) <= 15;
      });

    case 'enfants':
      // Recettes simples et populaires auprès des enfants
      const kidFriendlyKeywords = [
        'nuggets', 'pizza', 'crêpes', 'gâteau', 'omelette', 'sandwich', 
        'pain perdu', 'purée', 'spaghetti', 'pâtes'
      ];
      return recipes.filter(recipe => 
        kidFriendlyKeywords.some(keyword => 
          recipe.name.toLowerCase().includes(keyword) ||
          recipe.ingredients.some(ing => ing.toLowerCase().includes(keyword))
        )
      );

    case 'sucrees':
      // Recettes de desserts et plats sucrés
      return recipes.filter(recipe => 
        recipe.category === 'Dessert' ||
        recipe.name.toLowerCase().includes('sucré') ||
        recipe.name.toLowerCase().includes('crêpes') ||
        recipe.name.toLowerCase().includes('gâteau') ||
        recipe.name.toLowerCase().includes('pain perdu')
      );

    case 'economiques':
      // Recettes avec des ingrédients de base peu coûteux
      const economicalKeywords = [
        'œuf', 'pomme de terre', 'pâtes', 'riz', 'pain', 'lentilles',
        'oignon', 'carotte', 'thon', 'sardine', 'yaourt'
      ];
      return recipes.filter(recipe =>
        economicalKeywords.some(keyword =>
          recipe.ingredients.some(ing => ing.toLowerCase().includes(keyword))
        )
      );

    case 'vegetariennes':
      // Recettes sans viande ni poisson
      const meatKeywords = [
        'poulet', 'bœuf', 'agneau', 'veau', 'porc', 'dinde', 'canard',
        'thon', 'saumon', 'sardine', 'poisson', 'crevette', 'viande'
      ];
      return recipes.filter(recipe =>
        !meatKeywords.some(keyword =>
          recipe.name.toLowerCase().includes(keyword) ||
          recipe.ingredients.some(ing => ing.toLowerCase().includes(keyword))
        )
      );

    case 'traditionnelles':
      // Recettes traditionnelles algériennes/maghrébines
      const traditionalKeywords = [
        'couscous', 'tajine', 'chorba', 'boureka', 'makroudh',
        'baklawa', 'harira', 'rfissa', 'pastilla', 'chakhchoukha',
        'rechta', 'dolma', 'mahshi', 'mloukhia', 'ftira',
        'kalb el louz', 'zlabia', 'charak', 'bradj'
      ];
      return recipes.filter(recipe =>
        traditionalKeywords.some(keyword =>
          recipe.name.toLowerCase().includes(keyword) ||
          recipe.nameArabic?.toLowerCase().includes(keyword)
        )
      );

    case 'emporter':
      // Recettes facilement transportables
      const portableKeywords = [
        'sandwich', 'wrap', 'salade', 'quiche', 'muffin', 'cookie',
        'biscuit', 'cake', 'pizza'
      ];
      return recipes.filter(recipe =>
        portableKeywords.some(keyword =>
          recipe.name.toLowerCase().includes(keyword)
        )
      );

    case 'dietetiques':
      return recipes;

    default:
      return recipes;
  }
};

export const getThemeDisplayName = (theme: ThemeCategory): string => {
  const themeNames = {
    express: 'Recettes Express',
    enfants: 'Recettes Enfants',
    sucrees: 'Recettes Sucrées',
    economiques: 'Recettes Économiques',
    vegetariennes: 'Recettes Végétariennes',
    traditionnelles: 'Recettes Traditionnelles',
    emporter: 'Recettes à Emporter',
    dietetiques: 'Recettes Diététiques'
  };
  return themeNames[theme];
};